/**
 * /settings/permission-groups/$groupId (v7 — DESIGN-V7 §1, fiel à captura
 * tiflux-55-permissao-detalhe-top.png):
 * - breadcrumb "Configurações / Usuários / Grupos de permissões / <nome>";
 * - Nome e Descrição editáveis inline (lápis → input; salva no blur/Enter);
 * - DOIS painéis lado a lado — "Grupo de atendentes" e "Usuários" — cada um
 *   com select "Selecione … para adicioná-lo", lista com lixeira e
 *   "Não há dados" quando vazio;
 * - seções de checkboxes geradas de PERMISSION_SECTIONS em duas colunas, com
 *   "Selecionar todos" tri-state por seção e ícone ? (tooltip PERMISSION_HELP).
 *
 * Persistência: PUT /permission-groups/:id SUBSTITUI permissions/userIds/
 * attendantGroupIds — vínculos e checkboxes acumulam num rascunho local e um
 * botão Salvar fixo no rodapé (estado dirty) envia o estado completo; Nome e
 * Descrição salvam na hora (com as listas do SERVIDOR, para não commitar um
 * rascunho pendente junto).
 */
import { useState, type ReactNode } from 'react';
import { getRouteApi, Link } from '@tanstack/react-router';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { CircleHelp, Pencil, Trash2 } from 'lucide-react';
import {
  PERMISSION_HELP,
  PERMISSION_LABELS,
  PERMISSION_SECTIONS,
  type PermissionGroup,
  type PermissionGroupBody,
  type PermissionKey,
  type PermissionSection,
} from '@erp/shared';
import { api } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { EmptyState, Skeleton } from '@/components/ui/misc';
import { SettingsBreadcrumb } from '@/features/settings/settings-ui';
import { teamListQuery } from '@/features/team/team-queries';
import { groupsListOptions } from '@/features/groups/groups-queries';
import { desksListQuery } from '@/features/tickets/tickets-queries';
import {
  errorMessage,
  permissionGroupDetailOptions,
} from '@/features/permissions/permissions-queries';

const routeApi = getRouteApi('/_authenticated/settings/permission-groups/$groupId');

export function PermissionGroupDetailPage() {
  const { groupId: groupIdParam } = routeApi.useParams();
  const groupId = Number(groupIdParam);
  const groupQ = useQuery(permissionGroupDetailOptions(groupId));

  if (groupQ.isPending) {
    return (
      <div className="space-y-3" aria-busy>
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (groupQ.isError) {
    return (
      <div className="rounded-lg border bg-card shadow-xs">
        <EmptyState
          message={`Erro ao carregar o grupo de permissão: ${errorMessage(groupQ.error)}`}
        />
        <div className="flex justify-center pb-6">
          <Link
            to="/settings/permission-groups"
            className="text-sm font-medium text-primary hover:text-primary-pressed"
          >
            Voltar para Grupos de permissões
          </Link>
        </div>
      </div>
    );
  }

  // key: trocar de grupo remonta o rascunho local (sets de vínculos/keys).
  return <PermissionGroupDetail key={groupQ.data.id} group={groupQ.data} />;
}

function setsEqual(a: Set<number> | Set<string>, b: readonly (number | string)[]): boolean {
  return a.size === b.length && b.every((v) => (a as Set<number | string>).has(v));
}

function PermissionGroupDetail({ group }: { group: PermissionGroup }) {
  const queryClient = useQueryClient();
  const [groupsQ, usersQ, desksQ] = useQueries({
    queries: [groupsListOptions(), teamListQuery({ includeInactive: true }), desksListQuery()],
  });

  // Rascunho local do que o botão Salvar envia (o servidor é a baseline).
  const [permissions, setPermissions] = useState<Set<string>>(() => new Set(group.permissions));
  const [userIds, setUserIds] = useState<Set<number>>(() => new Set(group.userIds));
  const [attendantGroupIds, setAttendantGroupIds] = useState<Set<number>>(
    () => new Set(group.attendantGroupIds)
  );
  // spec 058: as mesas que o grupo enxerga (nenhuma = todas)
  const [deskIds, setDeskIds] = useState<Set<number>>(() => new Set(group.deskIds ?? []));

  const dirty =
    !setsEqual(permissions, group.permissions) ||
    !setsEqual(userIds, group.userIds) ||
    !setsEqual(attendantGroupIds, group.attendantGroupIds) ||
    !setsEqual(deskIds, group.deskIds ?? []);

  const save = useMutation({
    mutationFn: async (body: PermissionGroupBody) => {
      const res = await api<PermissionGroup>(`/permission-groups/${group.id}`, {
        method: 'PUT',
        body,
      });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: (updated) => {
      // Baseline nova = resposta do PUT; os sets locais são comparados a ela.
      // NÃO reseta o rascunho aqui — saveMeta (Nome/Descrição) não pode
      // descartar checkboxes/vínculos pendentes; só o Salvar do rodapé
      // sincroniza o rascunho (no onSuccess da própria chamada, abaixo).
      queryClient.setQueryData(permissionGroupDetailOptions(group.id).queryKey, updated);
      void queryClient.invalidateQueries({ queryKey: ['erp', 'permission-groups', 'list'] });
    },
  });

  /** Nome/Descrição salvam na hora — com as listas do SERVIDOR (não o rascunho). */
  const saveMeta = (patch: { name?: string; description?: string | null }) => {
    save.mutate({
      name: patch.name ?? group.name,
      description: patch.description !== undefined ? patch.description : group.description,
      permissions: group.permissions as PermissionKey[],
      userIds: group.userIds,
      attendantGroupIds: group.attendantGroupIds,
      deskIds: group.deskIds,
    });
  };

  /** Botão Salvar — estado completo do rascunho. */
  const saveDraft = () => {
    save.mutate(
      {
        name: group.name,
        description: group.description,
        permissions: [...permissions] as PermissionKey[],
        userIds: [...userIds],
        attendantGroupIds: [...attendantGroupIds],
        deskIds: deskIds.size > 0 ? [...deskIds] : null,
      },
      {
        onSuccess: (updated) => {
          // Rascunho passa a espelhar a baseline nova ⇒ dirty=false.
          setPermissions(new Set(updated.permissions));
          setUserIds(new Set(updated.userIds));
          setAttendantGroupIds(new Set(updated.attendantGroupIds));
          setDeskIds(new Set(updated.deskIds ?? []));
        },
      }
    );
  };

  const togglePermission = (key: PermissionKey, checked: boolean) => {
    setPermissions((prev) => {
      const next = new Set(prev);
      if (checked) next.add(key);
      else next.delete(key);
      return next;
    });
  };

  const toggleSection = (section: PermissionSection, checked: boolean) => {
    setPermissions((prev) => {
      const next = new Set(prev);
      for (const key of section.keys) {
        if (checked) next.add(key);
        else next.delete(key);
      }
      return next;
    });
  };

  const attendantGroups = groupsQ.data ?? [];
  // a equipe deste tenant (pedido da PoolTrack, 05/10): GET /users trazia também
  // a equipe de outras empresas, e a API recusa acrescentar quem está fora
  const staffUsers = usersQ.data?.items ?? [];
  const desks = (desksQ.data ?? []).filter((d) => d.active || deskIds.has(d.id));

  return (
    <div className="space-y-3 pb-16">
      <div className="rounded-lg border bg-card p-4 shadow-xs sm:p-5">
        <SettingsBreadcrumb
          section="Usuários"
          parent={{ label: 'Grupos de permissões', to: '/settings/permission-groups' }}
          page={group.name}
        />
        <h1 className="mt-2 text-xl leading-7 font-semibold">{group.name}</h1>

        {/* ── Nome / Descrição inline ──────────────────────────────────────── */}
        <div className="mt-4 grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2">
          <InlineTextField
            label="Nome"
            value={group.name}
            required
            saving={save.isPending}
            onSave={(v) => saveMeta({ name: v })}
          />
          <InlineTextField
            label="Descrição"
            value={group.description ?? ''}
            saving={save.isPending}
            onSave={(v) => saveMeta({ description: v.length > 0 ? v : null })}
          />
        </div>

        {/* ── Painéis de vínculo lado a lado ──────────────────────────────── */}
        <div className="mt-5 grid grid-cols-1 gap-6 lg:grid-cols-2">
          <AttachPanel
            title="Grupo de atendentes"
            placeholder="Selecione um grupo de atendentes para adicioná-lo"
            loading={groupsQ.isPending}
            options={attendantGroups.map((g) => ({ id: g.id, label: g.name }))}
            selectedIds={attendantGroupIds}
            onAdd={(id) => setAttendantGroupIds((prev) => new Set(prev).add(id))}
            onRemove={(id) =>
              setAttendantGroupIds((prev) => {
                const next = new Set(prev);
                next.delete(id);
                return next;
              })
            }
          />
          <AttachPanel
            title="Usuários"
            placeholder="Selecione um usuário para adicioná-lo"
            loading={usersQ.isPending}
            options={staffUsers.map((u) => ({ id: u.id, label: u.name, hint: u.email }))}
            selectedIds={userIds}
            onAdd={(id) => setUserIds((prev) => new Set(prev).add(id))}
            onRemove={(id) =>
              setUserIds((prev) => {
                const next = new Set(prev);
                next.delete(id);
                return next;
              })
            }
          />
        </div>

        {/* ── spec 058: as mesas que o grupo enxerga ─────────────────────────── */}
        <div className="mt-5 max-w-xl">
          <AttachPanel
            title="Mesas"
            placeholder="Selecione uma mesa para limitar o grupo a ela"
            loading={desksQ.isPending}
            options={desks.map((d) => ({ id: d.id, label: d.displayName }))}
            selectedIds={deskIds}
            onAdd={(id) => setDeskIds((prev) => new Set(prev).add(id))}
            onRemove={(id) =>
              setDeskIds((prev) => {
                const next = new Set(prev);
                next.delete(id);
                return next;
              })
            }
          />
          <p className="mt-1.5 text-xs text-muted-foreground">
            Sem nenhuma mesa, o grupo enxerga todas. Com mesas, quem está só em grupos com mesas
            enxerga apenas elas, nos tickets e nas conversas.
          </p>
        </div>

        {/* ── Seções de permissões em duas colunas ────────────────────────── */}
        <div className="mt-8 grid grid-cols-1 gap-x-12 gap-y-8 lg:grid-cols-2">
          {PERMISSION_SECTIONS.map((section) => (
            <PermissionSectionBlock
              key={section.id}
              section={section}
              selected={permissions}
              onToggleAll={(checked) => toggleSection(section, checked)}
              onToggle={togglePermission}
            />
          ))}
        </div>

        {save.error ? (
          <p className="mt-4 text-sm text-destructive">{errorMessage(save.error)}</p>
        ) : null}
      </div>

      {/* ── Rodapé fixo de salvamento (estado dirty) ─────────────────────── */}
      {dirty ? (
        <div className="sticky bottom-0 z-10 flex items-center justify-between gap-3 rounded-lg border bg-card px-4 py-3 shadow-md">
          <span className="text-sm text-muted-foreground">Alterações não salvas.</span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={save.isPending}
              onClick={() => {
                setPermissions(new Set(group.permissions));
                setUserIds(new Set(group.userIds));
                setAttendantGroupIds(new Set(group.attendantGroupIds));
                setDeskIds(new Set(group.deskIds ?? []));
              }}
            >
              Descartar
            </Button>
            <Button size="sm" disabled={save.isPending} onClick={saveDraft}>
              {save.isPending ? 'Salvando…' : 'Salvar'}
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Valor + lápis; clicar edita num input que salva no blur/Enter (Esc cancela).
 * Vazio exibe "--" como no TiFlux.
 */
function InlineTextField({
  label,
  value,
  required,
  saving,
  onSave,
}: {
  label: string;
  value: string;
  required?: boolean;
  saving: boolean;
  onSave: (value: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  const commit = () => {
    setEditing(false);
    const next = draft.trim();
    if (next === value.trim()) return;
    if (required && next.length === 0) return;
    onSave(next);
  };

  return (
    <div className="min-w-0">
      <div className="text-[13px] font-medium text-muted-foreground">{label}</div>
      {editing ? (
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commit();
            } else if (e.key === 'Escape') {
              setDraft(value);
              setEditing(false);
            }
          }}
          autoFocus
          className="mt-1 h-8"
          aria-label={label}
        />
      ) : (
        <div className="mt-1 flex min-w-0 items-center gap-1.5">
          <span className="truncate text-sm">
            {value.length > 0 ? value : <span className="text-muted-foreground">--</span>}
          </span>
          <Button
            variant="ghost"
            size="icon-xs"
            className="shrink-0 text-primary"
            title={`Editar ${label.toLowerCase()}`}
            disabled={saving}
            onClick={() => {
              setDraft(value);
              setEditing(true);
            }}
          >
            <Pencil className="size-3.5" />
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * Painel de vínculo: select "Selecione … para adicioná-lo" (some da lista o
 * que já foi adicionado) + lista com lixeira; vazio = "Não há dados".
 */
function AttachPanel({
  title,
  placeholder,
  loading,
  options,
  selectedIds,
  onAdd,
  onRemove,
}: {
  title: string;
  placeholder: string;
  loading: boolean;
  options: { id: number; label: string; hint?: string }[];
  selectedIds: Set<number>;
  onAdd: (id: number) => void;
  onRemove: (id: number) => void;
}) {
  const byId = new Map(options.map((o) => [o.id, o]));
  const available = options.filter((o) => !selectedIds.has(o.id));
  const selected = [...selectedIds]
    .map((id) => byId.get(id) ?? { id, label: `#${id}` })
    .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));

  return (
    <div className="min-w-0">
      {/* value="" mantém o placeholder — cada escolha só ADICIONA à lista. */}
      <Select value="" onValueChange={(v) => onAdd(Number(v))} disabled={loading}>
        <SelectTrigger size="sm" className="w-full text-muted-foreground">
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {available.length === 0 ? (
            <div className="px-2 py-1.5 text-sm text-muted-foreground">
              {loading ? 'Carregando…' : 'Nada para adicionar'}
            </div>
          ) : (
            available.map((o) => (
              <SelectItem key={o.id} value={String(o.id)}>
                {o.label}
              </SelectItem>
            ))
          )}
        </SelectContent>
      </Select>

      <div className="mt-3 overflow-hidden rounded-md border border-divider">
        <div className="flex h-9 items-center bg-muted px-3 text-[13px] font-semibold">{title}</div>
        {selected.length === 0 ? (
          <EmptyState message="Não há dados" className="h-32" />
        ) : (
          <ul className="max-h-56 overflow-y-auto">
            {selected.map((o) => (
              <li
                key={o.id}
                className="flex items-center gap-2 border-b border-divider px-3 py-2 text-sm last:border-0"
              >
                <span className="min-w-0 truncate font-medium">{o.label}</span>
                {'hint' in o && o.hint ? (
                  <span className="min-w-0 truncate text-xs text-muted-foreground">{o.hint}</span>
                ) : null}
                <Button
                  variant="ghost"
                  size="icon-xs"
                  className="ml-auto shrink-0 text-muted-foreground hover:text-destructive"
                  title={`Remover ${o.label}`}
                  onClick={() => onRemove(o.id)}
                >
                  <Trash2 className="size-3.5" />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/** Seção de checkboxes: título com divisor, "Selecionar todos" tri-state e itens com ícone ?. */
function PermissionSectionBlock({
  section,
  selected,
  onToggleAll,
  onToggle,
}: {
  section: PermissionSection;
  selected: Set<string>;
  onToggleAll: (checked: boolean) => void;
  onToggle: (key: PermissionKey, checked: boolean) => void;
}) {
  const checkedCount = section.keys.filter((k) => selected.has(k)).length;
  const allChecked = checkedCount === section.keys.length;
  const someChecked = checkedCount > 0 && !allChecked;

  return (
    <section className="min-w-0">
      <div className="flex items-center gap-3">
        <div className="h-px w-6 shrink-0 bg-divider" aria-hidden />
        <h2 className="shrink-0 text-[15px] font-semibold">{section.label}</h2>
        <div className="h-px flex-1 bg-divider" aria-hidden />
      </div>

      <label className="mt-3 flex w-fit cursor-pointer items-center gap-2.5 pl-9 text-sm font-medium">
        <Checkbox
          checked={allChecked ? true : someChecked ? 'indeterminate' : false}
          onCheckedChange={(checked) => onToggleAll(checked === true)}
          aria-label={`Selecionar todas as ${section.label.toLowerCase()}`}
        />
        Selecionar todos
      </label>

      <ul className="mt-3 space-y-1">
        {section.keys.map((key) => (
          <li key={key} className="flex items-center gap-2.5">
            <HelpTip>{PERMISSION_HELP[key]}</HelpTip>
            <label className="flex min-w-0 cursor-pointer items-center gap-2.5 text-sm">
              <Checkbox
                checked={selected.has(key)}
                onCheckedChange={(checked) => onToggle(key, checked === true)}
              />
              <span className="min-w-0 truncate">{PERMISSION_LABELS[key]}</span>
            </label>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** Ícone ? com tooltip — explicação curta da permissão (PERMISSION_HELP). */
function HelpTip({ children }: { children: ReactNode }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label="O que esta permissão faz"
          className="shrink-0 cursor-help text-muted-foreground/70 transition-colors hover:text-muted-foreground"
        >
          <CircleHelp className="size-4" />
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-72">
        {children}
      </TooltipContent>
    </Tooltip>
  );
}
